import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);

    // Small delay to ensure children are rendered and queryable
    requestAnimationFrame(() => {
      this.#restoreToggleState();
    });

    this.refs.details.addEventListener('toggle', (event) => {
      if (this.refs.details.open) {
        this.#restoreToggleState();
      }
    });
  }

  /**
   * Restores the Men's/Women's toggle state from sessionStorage
   */
  #restoreToggleState() {
    const savedTarget = sessionStorage.getItem('bbd-header-drawer-menu-target') || 'mens';
    this.toggleMenu(savedTarget);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle(e) {
    if (this.isOpen) {
      e.preventDefault();
      this.close();
      return;
    }

    this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);

    if (details === this.refs.details) {
      this.#restoreToggleState();
    } else if (event) {
      event.preventDefault();
      details.setAttribute('open', '');
    } else {
      details.setAttribute('open', '');
    }

    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (details !== this.refs.details) {
        if (!details.closest('.menu-drawer__navigation--single-item')) {
          this.querySelector('.menu-drawer').classList.add('submenu-open');
        }
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    if (details !== this.refs.details) {
      const openSubmenus = this.querySelectorAll('details[open]:not(#Details-menu-drawer-container):not(accordion-custom > details)');
      if (openSubmenus.length <= 1) {
        this.querySelector('.menu-drawer').classList.remove('submenu-open');
      }
    } else {
      this.querySelector('.menu-drawer').classList.remove('submenu-open');
    }

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);

          // Force a state restoration to ensure everything is visible for next open
          this.#restoreToggleState();
        } else {
          trapFocus(this.refs.details);
        }
        reset(details);
      },
      { subtree: false }
    );
  }

  toggleMenu(eventOrTarget) {
    const target = typeof eventOrTarget === 'string' ? eventOrTarget : eventOrTarget.target.dataset.target;
    if (!target) return;

    sessionStorage.setItem('bbd-header-drawer-menu-target', target);

    const toggleButtons = this.querySelectorAll('.menu-drawer__toggle-button');
    toggleButtons.forEach((button) => {
      const isActive = button.dataset.target === target;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', isActive);
    });

    const menuContainers = this.querySelectorAll('.menu-drawer__navigation-container');
    menuContainers.forEach((container) => {
      if (container.dataset.menu === target) {
        container.removeAttribute('hidden');
      } else {
        container.setAttribute('hidden', '');
      }
    });

    const wrapper = this.querySelector('.menu-drawer__navigation-wrapper');
    if (wrapper) {
      wrapper.dataset.activeMenu = target;
    }
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  if (!element.closest('.menu-drawer__navigation--single-item')) {
    element.removeAttribute('open');
  }
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
